"use client";

import { useId, useState, useRef, useEffect } from "react";
import { Info } from "lucide-react";

/**
 * Kleines Hover/Focus-Tooltip mit Info-Icon. Funktioniert sowohl mit Maus
 * (hover) als auch Tastatur (focus) – keine externe Tooltip-Lib nötig.
 *
 * Gedacht für Sicherheits-/Erklär-Hinweise neben Form-Labels und Buttons,
 * damit der Inhalt nicht ständig auf der Seite Platz wegnimmt.
 *
 * Beispiel:
 *   <label>Passwort <HoverInfo>Mind. 12 Zeichen, einzigartig …</HoverInfo></label>
 */
export function HoverInfo({
  children,
  side = "top",
  size = "sm",
  label = "Mehr Infos",
  tipSize = "xs",
}: {
  children: React.ReactNode;
  side?: "top" | "bottom" | "left" | "right";
  size?: "sm" | "md";
  label?: string;
  /** Schriftgröße des Tooltips – „sm“ (14 px) für Erklärtexte auf öffentlichen Seiten. */
  tipSize?: "xs" | "sm";
}) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  // Auf mobile (touch) auch per Tap zugänglich machen + Outside-Tap schließt
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent | TouchEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [open]);

  const iconCls = size === "md" ? "h-4 w-4" : "h-3.5 w-3.5";

  // Position des Tooltips relativ zum Trigger
  const tipPos: Record<typeof side, string> = {
    top: "bottom-full left-1/2 mb-1.5 -translate-x-1/2",
    bottom: "top-full left-1/2 mt-1.5 -translate-x-1/2",
    left: "right-full top-1/2 mr-1.5 -translate-y-1/2",
    right: "left-full top-1/2 ml-1.5 -translate-y-1/2",
  };

  return (
    <span
      ref={ref}
      className="relative inline-flex align-middle"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        aria-label={label}
        aria-describedby={open ? id : undefined}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onClick={() => setOpen((o) => !o)}
        className="inline-flex h-5 w-5 items-center justify-center rounded-full transition-colors hover:bg-brand-500/10"
        style={{ color: "var(--text-quaternary)" }}
      >
        <Info className={iconCls} />
      </button>
      {open && (
        <span
          id={id}
          role="tooltip"
          className={`pointer-events-none absolute z-50 w-64 max-w-[calc(100vw-2rem)] rounded-lg border px-3 py-2 leading-relaxed shadow-lg ${tipSize === "sm" ? "text-sm font-normal" : "text-xs"} ${tipPos[side]}`}
          style={{
            backgroundColor: "var(--card-bg)",
            borderColor: "var(--border)",
            color: "var(--text-secondary)",
          }}
        >
          {children}
        </span>
      )}
    </span>
  );
}
